/** Every read the console makes, as TanStack Query hooks with shared keys.
 *
 *  Screens read through these instead of useEffect + useState, so a write in
 *  one place refreshes every screen showing it: a mutation invalidates the
 *  keys below. The keys are the ones the pages from before the redesign use
 *  (['dashboard', 30], ['googleStatus'], ['alerts'], ['campaigns', 30] …), so
 *  an old page and a new one share the cache while both are mounted. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type StatsBy } from '@/api/client'
import { useJobRuns } from '@/admin-design/_common/jobs/api'

export { useJobRuns }

/** The live screens (오늘, the attention strip, the badges) poll this often. */
export const LIVE_MS = 30_000
/** The autopilot's recommendations (as the old campaign page polled). */
export const AUTOPILOT_MS = 60_000

/** A stats query, as `/api/report/stats` takes it. */
export interface StatsQuery {
  by: StatsBy
  from?: string
  to?: string
  limit?: number
  /** `by=app` only: keep apps with no traffic at all. */
  include_zero?: boolean
}

export const qk = {
  me: ['me'] as const,
  version: ['version'] as const,
  dashboard: (days: number) => ['dashboard', days] as const,
  googleStatus: ['googleStatus'] as const,
  alerts: ['alerts'] as const,
  /** The old calendar and day report file their stats as
   *  ['report-stats', by, from, to]; a limit, when given, comes last. */
  stats: (q: StatsQuery) => {
    const key: unknown[] = ['report-stats', q.by, q.from ?? '', q.to ?? '']
    if (q.limit) key.push(q.limit)
    if (q.include_zero) key.push('include_zero')
    return key as readonly unknown[]
  },
  campaigns: (days: number) => ['campaigns', days] as const,
  appleApps: (days: number) => ['apple-apps', days] as const,
  apps: ['apps'] as const,
  budgetPools: ['budget-pools'] as const,
  writeLog: (limit: number) => ['write-log', limit] as const,
  autopilot: ['autopilot'] as const,
  statsToken: ['stats-token'] as const,
}

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: api.me, staleTime: 5 * 60_000 })
}

/** The build the server runs, for the sidebar footer. */
export function useVersion() {
  return useQuery({ queryKey: qk.version, queryFn: api.version, staleTime: Infinity })
}

/** The 30-day app ledger 오늘 lists its top five from (spend of campaigns
 *  mapped to each app). Its period totals and trend count mapped spend only,
 *  so no screen reads them: totals come from `useStats` (every campaign's
 *  spend). Polled with 오늘. */
export function useDashboard(days = 30) {
  return useQuery({ queryKey: qk.dashboard(days), queryFn: () => api.dashboard(days), refetchInterval: LIVE_MS })
}

/** The Google connection: account, AdMob publisher ID, Ads link, counts and
 *  the last ok sync. */
export function useGoogleStatus() {
  return useQuery({ queryKey: qk.googleStatus, queryFn: api.googleStatus, refetchInterval: LIVE_MS })
}

export function useAlerts() {
  return useQuery({ queryKey: qk.alerts, queryFn: api.alerts, refetchInterval: LIVE_MS })
}

/** The last five sync runs (job_runs `sync`) with their details — what
 *  lib/spend.ts judges the sync and spend state from. Read per job, so the
 *  6-hourly session purge never pushes them out. */
export function useSyncRuns() {
  const q = useJobRuns({ job: 'sync', limit: 5, refetchMs: LIVE_MS })
  return { ...q, runs: q.data?.runs }
}

/** `/api/report/stats`: totals and rows by day, month, app, country or app ×
 *  month over a range. Off until the range is known (`enabled`); `live`
 *  polls it like the other 오늘 reads.
 *
 *  Spend: `totals.cost` and the day and month rows count every campaign;
 *  app rows count only campaigns mapped to the app, and `unmapped_cost` is
 *  the rest. A total on screen reads day/month/total, a per-app figure reads
 *  app rows — never the other way round. */
export function useStats(q: StatsQuery, opts: { enabled?: boolean; live?: boolean } = {}) {
  return useQuery({
    queryKey: qk.stats(q),
    queryFn: () => api.reportStats(q),
    enabled: opts.enabled ?? true,
    refetchInterval: opts.live ? LIVE_MS : undefined,
  })
}

export function useCampaigns(days = 30) {
  return useQuery({ queryKey: qk.campaigns(days), queryFn: () => api.campaigns(days) })
}

/** Apple Ads results per app, with App Store proceeds on the days ads ran. */
export function useAppleApps(days = 30) {
  return useQuery({ queryKey: qk.appleApps(days), queryFn: () => api.appleApps(days) })
}

/** The budget pools with their members' spend. */
export function useBudgetPools() {
  return useQuery({ queryKey: qk.budgetPools, queryFn: api.budgetPools, refetchInterval: LIVE_MS })
}

/** Every write to the ad platform, newest first. */
export function useWriteLog(limit = 50) {
  return useQuery({ queryKey: qk.writeLog(limit), queryFn: () => api.writeLog(limit) })
}

/** The AdMob apps: name, platform, package, AdMob app ID. */
export function useApps() {
  return useQuery({ queryKey: qk.apps, queryFn: api.apps })
}

/** The Ads account currency and today's rate, for the budget previews. */
export function useFx() {
  return useQuery({ queryKey: ['fx'] as const, queryFn: api.fx, staleTime: 10 * 60_000 })
}

export function useAutopilot() {
  return useQuery({ queryKey: qk.autopilot, queryFn: api.autopilot, refetchInterval: AUTOPILOT_MS })
}

// ─── Writes ───────────────────────────────────────────────────────────────
// Every one below that reaches Google is marked "외부 호출": the dev server
// never presses it, tests mock it.

/** 외부 호출 — one sync pass now (AdMob + Google Ads). Every figure may
 *  change, so the whole cache goes stale, as on the old Google page. */
export function useSyncNow() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: () => api.syncNow(), onSettled: () => qc.invalidateQueries() })
}

export function useReadAlert() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => api.readAlert(id), onSuccess: () => qc.invalidateQueries({ queryKey: qk.alerts }) })
}

/** 외부 호출 — creates the campaign in Google Ads, paused. */
export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createCampaign>[0]) => api.createCampaign(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
      qc.invalidateQueries({ queryKey: ['write-log'] })
    },
  })
}

/** 외부 호출 — runs or pauses a campaign in Google Ads. */
export function useSetCampaignStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'ENABLED' | 'PAUSED' }) => api.setCampaignStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
      qc.invalidateQueries({ queryKey: qk.autopilot })
      qc.invalidateQueries({ queryKey: qk.budgetPools })
      qc.invalidateQueries({ queryKey: ['write-log'] })
    },
  })
}

/** 외부 호출 — sets a campaign's daily budget in Google Ads. */
export function useSetCampaignBudget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, amount }: { id: number; amount: number }) => api.setCampaignBudget(id, amount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
      qc.invalidateQueries({ queryKey: qk.autopilot })
      qc.invalidateQueries({ queryKey: qk.budgetPools })
      qc.invalidateQueries({ queryKey: ['write-log'] })
    },
  })
}

/** Local only — ties a campaign's spend to an app. */
export function useMapCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, appId }: { id: number; appId: number | null }) => api.mapCampaign(id, appId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['report-stats'] })
    },
  })
}

/** The pool writes below are local only: nothing reaches Google. */
function usePoolInvalidation() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: qk.budgetPools })
    qc.invalidateQueries({ queryKey: ['campaigns'] })
  }
}

export function useCreateBudgetPool() {
  const done = usePoolInvalidation()
  return useMutation({ mutationFn: (body: Parameters<typeof api.createBudgetPool>[0]) => api.createBudgetPool(body), onSuccess: done })
}

export function useUpdateBudgetPool() {
  const done = usePoolInvalidation()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof api.updateBudgetPool>[1] }) => api.updateBudgetPool(id, body),
    onSuccess: done,
  })
}

export function useDeleteBudgetPool() {
  const done = usePoolInvalidation()
  return useMutation({ mutationFn: (id: number) => api.deleteBudgetPool(id), onSuccess: done })
}

export function useSetCampaignPool() {
  const done = usePoolInvalidation()
  return useMutation({
    mutationFn: ({ id, poolId }: { id: number; poolId: number | null }) => api.setCampaignPool(id, poolId),
    onSuccess: done,
  })
}

/** 외부 호출 — trades the consent code for tokens and discovers apps and
 *  campaigns. */
export function useGoogleConnect() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Parameters<typeof api.googleConnect>[0]) => api.googleConnect(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.googleStatus })
      qc.invalidateQueries({ queryKey: qk.apps })
    },
  })
}

/** 외부 호출 — revokes the token and deletes every synced row. */
export function useGoogleDisconnect() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: () => api.googleDisconnect(), onSettled: () => qc.invalidateQueries() })
}

/** Local only — the Ads customer, developer token and manager (MCC) ids. */
export function useSetAdsConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ customer, devToken, loginCustomer }: { customer: string; devToken: string; loginCustomer?: string }) =>
      api.setAdsConfig(customer, devToken, loginCustomer),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.googleStatus }),
  })
}

/** Local only — the AdMob publisher ID ("pub-…"). */
export function useSetAdmobAccount() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (pub: string) => api.setAdmobAccount(pub), onSuccess: () => qc.invalidateQueries({ queryKey: qk.googleStatus }) })
}
